"use client";

import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
interface Project {
  title: string;
  url: string;
  image?: string; // external screenshot url
  note?: string; // e.g., (work in progress)
}

const projects: Project[] = [
  { title: "MIZANO", url: "https://www.mizano.ge/", image: "https://i.ibb.co/NdnnBwFv/mizano.png" },
  { title: "Santeqservice", url: "https://santeqservice.ge/", image: "https://i.ibb.co/d000QLHM/santeqservice.png" },
  { title: "Dijasfoods", url: "https://dijasfoods.com/", image: "/dijasfoods.png" },
  { title: "Artisaire", url: "https://artisaire.com/", image: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/91c097f61c7177504858835aeba46628.png" },
  { title: "Veltrix", url: "https://aesthetic-cupcake-6aa2d8.netlify.app/", image: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/139da96589896ee935d99615f9756134.png" },
  { title: "Smart Ultrasound", url: "https://smartultrasound.ge/", image: "https://i.ibb.co/cKC4NYw5/smartultrasound.png" },
  { title: "Smart House", url: "https://www.shg.ge/", image: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/27675aae0d736a1416fe69e98339fab4.png" },
  { title: "TFZ", url: "https://tfz.ge/", image: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/808b2759c94a26176662676a72658b02.png" },
  { title: "Tripspace", url: "https://tripspace.io/", note: "work in progress", image: "https://i.ibb.co/9kzw793T/tripspace.png" },
  { title: "TDA", url: "https://tda.ge/", image: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/85f494b01fcadeacc880fec8b7033ff9.png" },
  { title: "CHRONOS", url: "https://celebrated-dragon-3d3dec.netlify.app/", image: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/6d5e08918de38f991e0579d0f5823f68.png" },
  { title: "Perfy", url: "https://drinkperfy.com/", image: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/f9f26813ca9d2c1bbd973ac42c5a62a6.png" },
  { title: "Kolkhi Shop", url: "https://kolkhi.shop/", image: "https://donpuppy.my.canva.site/shota-iasashvili/_assets/media/e135717d7cbe917cfea14220c79fdf20.png" },
  { title: "Greencomfort", url: "https://greencomfort.ge/", image: "https://i.ibb.co/rKHHX5MW/green.png" },
  { title: "NTF", url: "https://ntf.ge/", image: "https://i.ibb.co/mr3wWLm5/ntf.png" },
];

export default function Works() {

  return (
    <section
      id="works"
      className="relative isolate overflow-hidden py-24 md:py-36"
      style={{ contain: "paint" }}
    >
      {/* Subtle overlay for content readability */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
      >
        {/* Light overlay to ensure text readability without blocking the global gradient */}
        <div className="absolute inset-0 section-overlay" />
      </div>


      {/* Content */}
      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        <div className="mb-10 md:mb-14">
          <motion.h3 
            className="text-3xl md:text-5xl font-bold"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Selected Work
          </motion.h3>
          <motion.p 
            className="mt-3 text-foreground/70 md:text-lg"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            A collection of shipped projects and in-progress explorations with screenshots.
          </motion.p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p, i) => (
            <ProjectCard key={p.url} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ project, index }: { project: Project; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative overflow-hidden rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-border transition-all duration-300"
    >
      {/* Clickable Image */}
      <a
        href={project.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block aspect-video overflow-hidden bg-muted cursor-pointer"
      >
        {project.image ? (
          <img
            src={project.image}
            alt={`${project.title} screenshot`}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            No preview available
          </div>
        )}
        
        {/* Image hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
            <ExternalLink className="h-5 w-5 text-white" />
          </div>
        </div>
      </a>
      
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <h4 className="font-semibold truncate">{project.title}</h4>
          {project.note && (
            <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
              {project.note}
            </span>
          )}
        </div>
        
        {/* Modern Visit Site Button */}
        <a
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group/btn relative inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-accent/20 to-accent2/20 border border-accent/30 backdrop-blur-sm text-sm font-medium text-foreground hover:from-accent/30 hover:to-accent2/30 hover:border-accent/50 transition-all duration-300 overflow-hidden"
        >
          {/* Button background glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-accent/10 to-accent2/10 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300" />
          
          {/* Button content */}
          <span className="relative z-10">Visit Site</span>
          <ExternalLink className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          
          {/* Animated border */}
          <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-accent to-accent2 opacity-0 group-hover/btn:opacity-20 transition-opacity duration-300" />
        </a>
      </div>
      
      {/* Card hover gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-background/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </motion.div>
  );
}